import { createId } from '../lib/ids';
import type { Layer, Scene, SceneObject } from '../domain/schemas';

/**
 * Starter scenes built only from procedural shapes (no downloaded artwork). Each preset is
 * an ordinary, fully editable scene: three layers (Background, Midground, Foreground) and
 * plain objects. `idFor` makes the ids injectable so tests can build presets
 * deterministically.
 */
export type ScenePresetId = 'neon-city' | 'dark-studio' | 'forest';

export const SCENE_PRESETS: Record<ScenePresetId, { label: string; description: string }> = {
  'neon-city': { label: 'Neon City', description: 'Night skyline with glowing windows and a neon sign' },
  'dark-studio': { label: 'Dark Studio', description: 'Moody stage with spotlights and a floor' },
  forest: { label: 'Forest Landscape', description: 'Daylight hills, pine trees and a sun' },
};
export const SCENE_PRESET_IDS = Object.keys(SCENE_PRESETS) as ScenePresetId[];

type IdFor = (prefix: string) => string;

const W = 1280;
const H = 720;

/** Small seeded PRNG so presets are identical every time. */
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export function buildPresetScene(preset: ScenePresetId, idFor: IdFor = createId): Scene {
  const layers: Layer[] = ['Background', 'Midground', 'Foreground'].map((name) => ({ id: idFor('lyr'), name, visible: true, locked: false }));
  const [bg, mid, fg] = layers.map((l) => l.id) as [string, string, string];
  const objects: SceneObject[] = [];
  const base = (layerId: string, name: string, x: number, y: number, width: number, height: number) => ({
    id: idFor('obj'),
    name,
    layerId,
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
    width: Math.max(1, Math.round(width * 100) / 100),
    height: Math.max(1, Math.round(height * 100) / 100),
    rotation: 0,
    opacity: 1,
    anchorX: 0.5,
    anchorY: 0.5,
    visible: true,
    locked: false,
    groupId: null,
  });
  const rect = (layerId: string, name: string, x: number, y: number, w: number, h: number, fill: string, extra: Partial<SceneObject> = {}) =>
    objects.push({ ...base(layerId, name, x, y, w, h), type: 'rect', style: { fill, stroke: null }, cornerRadius: 0, ...extra } as SceneObject);
  const circle = (layerId: string, name: string, x: number, y: number, d: number, fill: string, extra: Partial<SceneObject> = {}) =>
    objects.push({ ...base(layerId, name, x, y, d, d), type: 'circle', style: { fill, stroke: null }, ...extra } as SceneObject);
  const ellipse = (layerId: string, name: string, x: number, y: number, w: number, h: number, fill: string, extra: Partial<SceneObject> = {}) =>
    objects.push({ ...base(layerId, name, x, y, w, h), type: 'ellipse', style: { fill, stroke: null }, ...extra } as SceneObject);
  const polygon = (layerId: string, name: string, x: number, y: number, w: number, h: number, fill: string, points: [number, number][], extra: Partial<SceneObject> = {}) =>
    objects.push({ ...base(layerId, name, x, y, w, h), type: 'polygon', style: { fill, stroke: null }, points, ...extra } as SceneObject);
  const text = (layerId: string, name: string, x: number, y: number, w: number, h: number, value: string, color: string, fontSize: number) =>
    objects.push({ ...base(layerId, name, x, y, w, h), type: 'text', text: value, fontSize, fontFamily: 'sans', bold: true, align: 'center', color } as SceneObject);

  let scene: Pick<Scene, 'name' | 'background' | 'gradient'>;
  switch (preset) {
    case 'neon-city': {
      scene = { name: 'Neon City', background: '#1e0b3a', gradient: { to: '#05010f', angle: 90 } };
      circle(bg, 'Moon', 1010, 70, 110, '#fdf4ff', { opacity: 0.9 });
      const r = rng(7);
      // Back skyline (darker) then front skyline with lit windows.
      for (let i = 0, x = -20; x < W; i++) {
        const w = 90 + Math.round(r() * 80);
        const h = 220 + Math.round(r() * 220);
        rect(bg, `Far tower ${i + 1}`, x, H - 120 - h, w, h, '#2e1065', { opacity: 0.85 });
        x += w - 10;
      }
      const neon = ['#22d3ee', '#f472b6', '#a3e635', '#facc15'];
      for (let i = 0, x = 10; x < W - 40; i++) {
        const w = 110 + Math.round(r() * 70);
        const h = 160 + Math.round(r() * 200);
        const top = H - 110 - h;
        rect(mid, `Tower ${i + 1}`, x, top, w, h, '#120826');
        const color = neon[i % neon.length]!;
        for (let row = 0; row < Math.min(5, Math.floor(h / 55)); row++) {
          for (let col = 0; col < 2; col++) {
            if (r() < 0.35) continue;
            rect(mid, `Window ${i + 1}.${row + 1}.${col + 1}`, x + 18 + col * (w / 2 - 6), top + 22 + row * 52, w / 2 - 30, 24, color, { opacity: 0.8 });
          }
        }
        x += w + 18;
      }
      rect(fg, 'Street', 0, H - 110, W, 110, '#0b0618');
      rect(fg, 'Street glow', 0, H - 112, W, 6, '#f472b6');
      rect(fg, 'Sign board', 460, 80, 360, 110, '#0b0618', { style: { fill: '#0b0618', stroke: { color: '#22d3ee', width: 4 } } } as Partial<SceneObject>);
      text(fg, 'Neon sign', 470, 95, 340, 80, 'NEON', '#f472b6', 64);
      break;
    }
    case 'dark-studio': {
      scene = { name: 'Dark Studio', background: '#18181b', gradient: { to: '#020203', angle: 90 } };
      rect(bg, 'Back wall panel', 140, 60, 1000, 440, '#27272a', { cornerRadius: 12 } as Partial<SceneObject>);
      for (let i = 0; i < 6; i++) rect(bg, `Acoustic panel ${i + 1}`, 190 + i * 155, 110, 110, 160, '#3f3f46', { cornerRadius: 6 } as Partial<SceneObject>);
      polygon(mid, 'Left spotlight', 160, -20, 420, 640, '#fef3c7', [[0.42, 0], [0.58, 0], [1, 1], [0, 1]], { opacity: 0.12 });
      polygon(mid, 'Right spotlight', 700, -20, 420, 640, '#c7d2fe', [[0.42, 0], [0.58, 0], [1, 1], [0, 1]], { opacity: 0.12 });
      rect(fg, 'Floor', 0, 560, W, 160, '#0a0a0a');
      ellipse(fg, 'Spot on floor', 420, 590, 440, 70, '#fef3c7', { opacity: 0.18 });
      rect(fg, 'Mic stand', 636, 330, 8, 250, '#71717a');
      ellipse(fg, 'Mic', 622, 300, 36, 50, '#a1a1aa');
      rect(fg, 'Monitor left', 160, 500, 150, 70, '#27272a', { cornerRadius: 8 } as Partial<SceneObject>);
      rect(fg, 'Monitor right', 970, 500, 150, 70, '#27272a', { cornerRadius: 8 } as Partial<SceneObject>);
      text(fg, 'On air', 1060, 24, 190, 50, 'ON AIR', '#ef4444', 32);
      break;
    }
    case 'forest': {
      scene = { name: 'Forest Landscape', background: '#7dd3fc', gradient: { to: '#e0f2fe', angle: 90 } };
      circle(bg, 'Sun', 1030, 60, 130, '#fde047');
      ellipse(bg, 'Cloud 1', 180, 90, 220, 70, '#ffffff', { opacity: 0.85 });
      ellipse(bg, 'Cloud 2', 560, 140, 180, 56, '#ffffff', { opacity: 0.75 });
      ellipse(bg, 'Far hill', -200, 400, 1000, 420, '#4d7c0f');
      ellipse(bg, 'Far hill 2', 500, 430, 1100, 400, '#65a30d');
      const r = rng(11);
      for (let i = 0; i < 9; i++) {
        const x = 30 + i * 140 + Math.round(r() * 40);
        const h = 200 + Math.round(r() * 120);
        const top = 560 - h;
        rect(mid, `Trunk ${i + 1}`, x + 38, 560 - 50, 24, 60, '#78350f');
        polygon(mid, `Pine ${i + 1}`, x, top, 100, h - 40, i % 2 ? '#166534' : '#15803d', [[0.5, 0], [1, 1], [0, 1]]);
      }
      rect(fg, 'Meadow', 0, 560, W, 160, '#4ade80');
      ellipse(fg, 'Pond', 760, 600, 300, 70, '#38bdf8', { opacity: 0.9 });
      for (let i = 0; i < 6; i++) circle(fg, `Flower ${i + 1}`, 90 + i * 110 + Math.round(r() * 30), 620 + Math.round(r() * 50), 18, i % 2 ? '#f472b6' : '#facc15');
      break;
    }
  }
  return {
    id: idFor('scn'),
    ...scene,
    speed: 1,
    elements: [],
    width: W,
    height: H,
    backgroundImage: null,
    layers,
    objects,
  };
}
